import { getFirestore, doc, getDoc, updateDoc, runTransaction, setDoc, collection, query, where, orderBy, limit, getDocs } from 'firebase/firestore';
import { getAuth } from 'firebase/auth'; // Add this import
import { encryptData, decryptData } from '../encryptionUtil';

const db = getFirestore();

/**
 * Service to handle invoice number generation with advanced resilience
 */
export const invoiceNumberService = {
  // In-memory cache for invoice numbers
  _cache: {
    lastInvoiceNumbers: {},
    expiryTime: 30 * 60 * 1000, // 30 minutes
    reservedNumbers: new Set(),
    lastUpdated: {}
  },
  
  /**
   * Generates an invoice number using a multi-layered approach for maximum reliability:
   * 1. Check local cache for reserved numbers
   * 2. Try transaction-based update to the dedicated counter document
   * 3. If that fails, fall back to user's lastInvoiceNumber field
   * 4. If that fails, query invoices to find highest number
   * 5. Ultimate fallback to timestamp-based number with user initials
   */
  async generateInvoiceNumber(userId, profile, options = { persistToDatabase: true }) {
    if (!userId || !profile) {
      throw new Error('User ID and profile are required');
    }

    console.log('Generating invoice number for:', profile.name);
    
    try {
      // First check cache for reserved numbers, but only if we're not saving
      // This prevents using cached numbers for actual invoice saves
      if (!options.forSaving) {
        const cachedNumber = this.getCachedInvoiceNumber(userId, profile);
        if (cachedNumber) {
          console.log('Using cached invoice number:', cachedNumber);
          
          // If we need to persist this cached number to database, do it now
          if (options.persistToDatabase) {
            await this.persistCachedNumberToDatabase(userId, profile, cachedNumber);
          }
          
          return cachedNumber;
        }
      }
      
      // Primary approach: Use dedicated counter document with transaction
      const invoiceNumber = await this.generateWithTransaction(userId, profile);
      if (invoiceNumber) {
        // Only cache if this isn't a final save operation
        if (!options.forSaving) {
          this.cacheInvoiceNumber(userId, invoiceNumber);
        }
        return invoiceNumber;
      }
      
      // Fallback 1: User's lastInvoiceNumber field approach (original method)
      const invoiceNumberFromUser = await this.generateFromUserDoc(userId, profile);
      if (invoiceNumberFromUser) {
        this.cacheInvoiceNumber(userId, invoiceNumberFromUser);
        return invoiceNumberFromUser;
      }
      
      // Fallback 2: Find highest invoice number in existing invoices
      const invoiceNumberFromInvoices = await this.findHighestInvoiceNumber(userId, profile);
      if (invoiceNumberFromInvoices) {
        this.cacheInvoiceNumber(userId, invoiceNumberFromInvoices);
        return invoiceNumberFromInvoices;
      }
      
      // Ultimate fallback: Generate from timestamp
      const timestampNumber = this.generateFromTimestamp(profile);
      this.cacheInvoiceNumber(userId, timestampNumber);
      return timestampNumber;
    } catch (error) {
      console.error('All invoice number generation methods failed:', error);
      // Last resort emergency fallback
      const emergencyNumber = this.generateEmergencyNumber(profile);
      this.cacheInvoiceNumber(userId, emergencyNumber);
      return emergencyNumber;
    }
  },

  /**
   * Persist a cached invoice number to the database to ensure consistency
   */
  async persistCachedNumberToDatabase(userId, profile, invoiceNumber) {
    try {
      console.log('Persisting cached invoice number to database:', invoiceNumber);
      
      // Extract number information
      const match = invoiceNumber.match(/^([A-Z]+)(\d+)$/);
      if (!match) return;
      
      const [_, prefix, numStr] = match;
      const number = parseInt(numStr, 10);
      
      // Update both the counter document and user document
      const counterRef = doc(db, 'invoiceCounters', userId);
      const userRef = doc(db, 'users', userId);
      
      // Use a transaction to ensure consistency
      await runTransaction(db, async (transaction) => {
        // Check current counter value first
        const counterDoc = await transaction.get(counterRef);
        
        // Only update if our number is higher (or document doesn't exist)
        if (!counterDoc.exists() || !counterDoc.data().lastNumber || counterDoc.data().lastNumber < number) {
          transaction.set(counterRef, {
            lastNumber: number,
            prefix: prefix,
            lastUpdated: new Date(),
            lastInvoiceId: invoiceNumber,
            userId
          }, { merge: true });
          
          // Also update user document
          transaction.update(userRef, {
            lastInvoiceNumber: encryptData(invoiceNumber),
            lastUpdated: new Date()
          });
          
          console.log('Database updated with cached number:', invoiceNumber);
        } else {
          console.log('Database already has equal or higher invoice number');
        }
      });
    } catch (error) {
      console.error('Error persisting cached invoice number to database:', error);
    }
  },

  /**
   * Get invoice number from cache if available
   */
  getCachedInvoiceNumber(userId, profile) {
    try {
      // Check if we have numbers in the cache
      const userInitials = this.getUserInitials(profile);
      const cacheKey = `${userId}_${userInitials}`;
      
      // Check if cache has expired
      const now = Date.now();
      if (this._cache.lastUpdated[cacheKey] && 
          (now - this._cache.lastUpdated[cacheKey] > this._cache.expiryTime)) {
        // Cache expired, clear it
        delete this._cache.lastInvoiceNumbers[cacheKey];
        delete this._cache.lastUpdated[cacheKey];
        return null;
      }
      
      // Return and remove a reserved number if available
      if (this._cache.reservedNumbers.size > 0) {
        for (const number of this._cache.reservedNumbers) {
          if (number.startsWith(userInitials)) {
            this._cache.reservedNumbers.delete(number);
            return number;
          }
        }
      }
      
      return null;
    } catch (error) {
      console.error('Error checking cached invoice number:', error);
      return null;
    }
  },
  
  /**
   * Cache invoice number for future use and reserve the next numbers
   */
  cacheInvoiceNumber(userId, invoiceNumber) {
    try {
      // Extract the numeric part and increment for future numbers
      const match = invoiceNumber.match(/^([A-Z]+)(\d+)$/);
      if (!match) return;
      
      const [_, prefix, numStr] = match;
      const num = parseInt(numStr, 10);
      const cacheKey = `${userId}_${prefix}`;
      
      // Store the last used number
      this._cache.lastInvoiceNumbers[cacheKey] = num;
      this._cache.lastUpdated[cacheKey] = Date.now();
      
      // Pre-reserve the next few invoice numbers to handle offline scenarios
      for (let i = 1; i <= 5; i++) {
        const nextNum = num + i;
        const nextInvoiceNumber = `${prefix}${String(nextNum).padStart(4, '0')}`;
        this._cache.reservedNumbers.add(nextInvoiceNumber);
      }
      
      // Also store in localStorage for persistence across refreshes
      try {
        const storageKey = `invoiceNumberCache_${userId}`;
        const cacheData = {
          lastNumbers: this._cache.lastInvoiceNumbers,
          reserved: Array.from(this._cache.reservedNumbers),
          updated: this._cache.lastUpdated,
          timestamp: Date.now()
        };
        localStorage.setItem(storageKey, JSON.stringify(cacheData));
      } catch (e) {
        console.warn('Failed to store invoice cache in localStorage:', e);
      }
    } catch (error) {
      console.error('Error caching invoice number:', error);
    }
  },
  
  /**
   * Initialize cache from localStorage on service initialization
   */
  initializeCache(userId) {
    try {
      const storageKey = `invoiceNumberCache_${userId}`;
      const cachedData = localStorage.getItem(storageKey);
      
      if (cachedData) {
        const data = JSON.parse(cachedData);
        
        // Check if cache is still valid (not older than expiry time)
        if (Date.now() - data.timestamp < this._cache.expiryTime) {
          this._cache.lastInvoiceNumbers = data.lastNumbers || {};
          this._cache.reservedNumbers = new Set(data.reserved || []);
          this._cache.lastUpdated = data.updated || {};
          console.log('Restored invoice number cache from localStorage');
          return true;
        } else {
          // Cache expired, clear it
          localStorage.removeItem(storageKey);
        }
      }
    } catch (e) {
      console.warn('Failed to initialize invoice cache from localStorage:', e);
    }
    return false;
  },

  /**
   * Primary method: Generate invoice number using a transaction on a dedicated counter document
   */
  async generateWithTransaction(userId, profile) {
    try {
      const counterRef = doc(db, 'invoiceCounters', userId);
      const userRef = doc(db, 'users', userId);
      
      console.log('Starting transaction to generate invoice number');

      // First, check the user document to find the last invoice number
      let lastKnownNumber = 0;
      let userInitials = this.getUserInitials(profile);
      
      try {
        const userDoc = await getDoc(userRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          if (userData.lastInvoiceNumber) {
            try {
              const decrypted = decryptData(userData.lastInvoiceNumber);
              console.log('Found last invoice number in user document:', decrypted);
              
              // Extract initials and number
              const match = decrypted.match(/^([A-Z]+)(\d+)$/);
              if (match) {
                const [_, storedInitials, numStr] = match;
                lastKnownNumber = parseInt(numStr, 10);
                // Use stored initials if they exist
                userInitials = storedInitials;
                console.log(`Extracted from user document: initials=${userInitials}, lastNumber=${lastKnownNumber}`);
              }
            } catch (error) {
              console.error('Failed to decrypt lastInvoiceNumber:', error);
            }
          }
        }
      } catch (error) {
        console.error('Error reading user document:', error);
      }
      
      // Now run the transaction
      const result = await runTransaction(db, async (transaction) => {
        // Get the counter document
        const counterDoc = await transaction.get(counterRef);
        
        let nextNumber = lastKnownNumber + 1; // Start with user document number + 1
        
        // If counter exists and has a higher number, use that instead
        if (counterDoc.exists()) {
          const counterData = counterDoc.data();
          if (counterData.lastNumber && counterData.lastNumber >= lastKnownNumber) {
            nextNumber = counterData.lastNumber + 1;
            console.log(`Counter document has higher number: ${counterData.lastNumber}, using ${nextNumber}`);
            
            // Use stored initials if available
            if (counterData.prefix) {
              userInitials = counterData.prefix;
            }
          } else {
            console.log(`Counter document has lower number (${counterData.lastNumber}) than user document (${lastKnownNumber}), using user number + 1`);
          }
        } else {
          console.log(`No counter document found, using number from user document + 1: ${nextNumber}`);
        }
        
        const newInvoiceNumber = `${userInitials}${String(nextNumber).padStart(4, '0')}`;
        
        // Update the counter document
        transaction.set(counterRef, {
          lastNumber: nextNumber,
          prefix: userInitials,
          lastUpdated: new Date(),
          lastInvoiceId: newInvoiceNumber,
          userId
        }, { merge: true });
        
        // Also update in user document for backward compatibility
        transaction.update(userRef, {
          lastInvoiceNumber: encryptData(newInvoiceNumber),
          lastUpdated: new Date()
        });
        
        return newInvoiceNumber;
      });
      
      console.log('Successfully generated invoice number with transaction:', result);
      return result;
    } catch (error) {
      console.error('Transaction-based invoice number generation failed:', error);
      
      // If it's specifically a permissions error, log it clearly
      if (error.code === 'permission-denied') {
        console.warn('Permission denied for transaction. This is likely a Firestore security rules issue.');
      }
      
      // Return null to try next method
      return null;
    }
  },

  /**
   * Fallback 1: Original method - get invoice number from user document
   */
  async generateFromUserDoc(userId, profile) {
    try {
      const docRef = doc(db, 'users', userId);
      const docSnap = await getDoc(docRef);
      
      let nextNumber = 1;
      const userInitials = this.getUserInitials(profile);

      if (docSnap.exists()) {
        const userData = docSnap.data();
        if (userData.lastInvoiceNumber) {
          try {
            const decryptedLastNumber = decryptData(userData.lastInvoiceNumber);
            const lastNumberMatch = decryptedLastNumber.match(/\d+$/);
            if (lastNumberMatch) {
              nextNumber = parseInt(lastNumberMatch[0], 10) + 1;
            }
          } catch (decryptError) {
            console.error('Error decrypting invoice number:', decryptError);
            return null; // Move to next method
          }
        }
      }

      const newInvoiceNumber = `${userInitials}${String(nextNumber).padStart(4, '0')}`;
      
      try {
        // Update the user record with the new encrypted invoice number
        const encryptedNewNumber = encryptData(newInvoiceNumber);
        await updateDoc(docRef, {
          lastInvoiceNumber: encryptedNewNumber,
          lastUpdated: new Date()
        });
        
        // Also update the counter document for future use - use set with merge
        // to handle cases where we don't have permission to create
        try {
          await setDoc(doc(db, 'invoiceCounters', userId), {
            lastNumber: nextNumber,
            prefix: userInitials,
            lastUpdated: new Date(),
            lastInvoiceId: newInvoiceNumber,
            userId
          }, { merge: true });
        } catch (counterError) {
          // Just log this error - we still have the invoice number
          console.warn('Failed to update invoice counter document but continuing:', counterError.message);
        }
      } catch (updateError) {
        console.error('Failed to update user document:', updateError);
        // Continue anyway since we have the number
      }

      console.log('Generated invoice number from user document:', newInvoiceNumber);
      return newInvoiceNumber;
    } catch (error) {
      console.error('User document invoice number generation failed:', error);
      return null; // Move to next method
    }
  },

  /**
   * Fallback 2: Query invoices collection to find highest invoice number
   * This implementation now properly queries the invoices collection
   */
  async findHighestInvoiceNumber(userId, profile) {
    try {
      console.log('Attempting to find highest invoice number in invoices collection');
      const userInitials = this.getUserInitials(profile);
      
      // Create a query for user's invoices
      const invoicesRef = collection(db, 'invoices');
      const userInvoicesQuery = query(
        invoicesRef,
        where('userId', '==', userId),
        // We don't filter by invoiceNumber pattern here because we need to examine all the user's invoices
        orderBy('createdAt', 'desc'), // Get newest first
        limit(50) // Limit to last 50 invoices to avoid excessive reads
      );
      
      const querySnapshot = await getDocs(userInvoicesQuery);
      
      if (querySnapshot.empty) {
        console.log('No invoices found for user');
        return null;
      }
      
      // Filter invoices with the user's initials and find highest number
      let highestNumber = 0;
      const invoiceNumberRegex = new RegExp(`^${userInitials}(\\d+)$`);
      
      querySnapshot.forEach(doc => {
        const invoice = doc.data();
        
        // Check if we have an invoice number that matches the pattern
        if (invoice.invoiceNumber) {
          const match = invoice.invoiceNumber.match(invoiceNumberRegex);
          if (match) {
            const invoiceNum = parseInt(match[1], 10);
            if (!isNaN(invoiceNum) && invoiceNum > highestNumber) {
              highestNumber = invoiceNum;
            }
          }
        }
      });
      
      if (highestNumber > 0) {
        // Found a valid highest number, increment by 1 for new invoice
        const nextNumber = highestNumber + 1;
        const newInvoiceNumber = `${userInitials}${String(nextNumber).padStart(4, '0')}`;
        
        console.log(`Found highest invoice number ${highestNumber}, generated new number: ${newInvoiceNumber}`);
        
        // Update the counter documents for future use
        try {
          // Update invoice counter document
          await setDoc(doc(db, 'invoiceCounters', userId), {
            lastNumber: nextNumber,
            prefix: userInitials,
            lastUpdated: new Date(),
            lastInvoiceId: newInvoiceNumber,
            userId,
            source: 'invoice-query-recovery' // Mark that this was recovered from invoices
          }, { merge: true });
          
          // Update user document as well for backward compatibility
          const userRef = doc(db, 'users', userId);
          await updateDoc(userRef, {
            lastInvoiceNumber: encryptData(newInvoiceNumber),
            lastUpdated: new Date()
          });
          
          console.log('Updated counter documents with recovered number');
        } catch (updateError) {
          console.error('Failed to update counters with recovered number:', updateError);
          // Continue anyway since we have the number
        }
        
        return newInvoiceNumber;
      }
      
      console.log('No valid invoice numbers found in query results');
      return null;
    } catch (error) {
      console.error('Error querying invoices collection:', error);
      return null; // Move to next method
    }
  },

  /**
   * Fallback 3: Generate from timestamp
   */
  generateFromTimestamp(profile) {
    const userInitials = this.getUserInitials(profile);
    const timestamp = Date.now().toString().slice(-5);
    const newInvoiceNumber = `${userInitials}${timestamp}`;
    
    console.log('Generated timestamp-based invoice number:', newInvoiceNumber);
    return newInvoiceNumber;
  },

  /**
   * Emergency fallback - last resort when all else fails
   */
  generateEmergencyNumber(profile) {
    const userInitials = this.getUserInitials(profile) || 'INV';
    const randomPart = Math.floor(Math.random() * 9000) + 1000;
    const timestamp = new Date().getSeconds();
    const newInvoiceNumber = `${userInitials}${randomPart}${timestamp}`;
    
    console.log('Generated emergency invoice number:', newInvoiceNumber);
    return newInvoiceNumber;
  },

  /**
   * Helper to extract user initials from profile
   */
  getUserInitials(profile) {
    if (!profile.name) return 'INV';
    
    const nameParts = profile.name.split(' ').filter(part => part.length > 0);
    
    if (nameParts.length >= 2) {
      return (nameParts[0][0] + nameParts[nameParts.length - 1][0]).toUpperCase();
    } else if (nameParts.length === 1) {
      return nameParts[0].substring(0, 2).toUpperCase();
    }
    
    return 'INV';
  },

  /**
   * Get a preview invoice number without persisting it
   */
  async getPreviewNumber(userId, profile) {
    try {
      // Get the current counter value without incrementing
      const counterRef = doc(db, 'invoiceCounters', userId);
      const userRef = doc(db, 'users', userId);
      const userInitials = this.getUserInitials(profile);
      
      let nextNumber = 1;
      
      // First check the counter document
      const counterDoc = await getDoc(counterRef);
      if (counterDoc.exists()) {
        const counterData = counterDoc.data();
        nextNumber = (counterData.lastNumber || 0) + 1;
      } else {
        // Check user document as fallback
        const userDoc = await getDoc(userRef);
        if (userDoc.exists() && userDoc.data().lastInvoiceNumber) {
          const decrypted = decryptData(userDoc.data().lastInvoiceNumber);
          const match = decrypted.match(/\d+$/);
          if (match) {
            nextNumber = parseInt(match[0], 10) + 1;
          }
        }
      }
      
      // Format the preview number
      return `${userInitials}${String(nextNumber).padStart(4, '0')}`;
    } catch (error) {
      console.error('Error getting preview number:', error);
      return 'PREVIEW';
    }
  }
};

// Initialize the cache from localStorage if available
try {
  const auth = getAuth();
  if (auth.currentUser) {
    invoiceNumberService.initializeCache(auth.currentUser.uid);
  }
} catch (e) {
  console.warn('Failed to initialize invoice number cache:', e);
}
